import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@/config/game-config';
import { isQualifyingScore } from '@/storage/score-repository';
import { NEON_FONTS } from '@/config/colors';

const FONT_TITLE = NEON_FONTS.TITLE;
const FONT_BODY = NEON_FONTS.BODY;
const PROMPT_DELAY_MS = 2000;

interface GameOverData {
  readonly score: number;
  readonly level: number;
}

export class GameOverScene extends Phaser.Scene {
  private overlay: Phaser.GameObjects.Rectangle | null = null;
  private gameOverText: Phaser.GameObjects.Text | null = null;
  private scoreText: Phaser.GameObjects.Text | null = null;
  private levelText: Phaser.GameObjects.Text | null = null;
  private promptText: Phaser.GameObjects.Text | null = null;
  private score = 0;
  private level = 1;
  private canAdvance = false;

  constructor() {
    super({ key: 'GameOverScene' });
  }

  init(data: GameOverData): void {
    this.score = data.score ?? 0;
    this.level = data.level ?? 1;
    this.canAdvance = false;
  }

  create(): void {
    this.cleanupObjects();

    // Semi-transparent dark overlay to dim the game underneath
    this.overlay = this.add.rectangle(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2,
      GAME_WIDTH,
      GAME_HEIGHT,
      0x000000,
      0.7,
    );
    this.overlay.setOrigin(0.5);

    // "GAME OVER" text - large and prominent with neon glow
    this.gameOverText = this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2 - 40,
      'GAME OVER',
      {
        fontFamily: FONT_TITLE,
        fontSize: '28px',
        fontStyle: 'bold',
        color: '#FF3355',
        shadow: { offsetX: 0, offsetY: 0, color: '#FF3355', blur: 16, fill: true },
      },
    ).setOrigin(0.5);

    // Score display
    this.scoreText = this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2 + 10,
      `SCORE: ${this.score}`,
      {
        fontFamily: FONT_BODY,
        fontSize: '16px',
        fontStyle: 'bold',
        color: '#FFFFFF',
      },
    ).setOrigin(0.5);

    // Level display
    this.levelText = this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2 + 35,
      `LEVEL: ${this.level}`,
      {
        fontFamily: FONT_BODY,
        fontSize: '14px',
        fontStyle: 'bold',
        color: '#00FF88',
        shadow: { offsetX: 0, offsetY: 0, color: '#00FF88', blur: 6, fill: true },
      },
    ).setOrigin(0.5);

    // "Press any key" prompt - appears after delay
    this.promptText = this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2 + 70,
      'PRESS ANY KEY',
      {
        fontFamily: FONT_BODY,
        fontSize: '13px',
        fontStyle: 'bold',
        color: '#FFE500',
        shadow: { offsetX: 0, offsetY: 0, color: '#FFE500', blur: 8, fill: true },
      },
    ).setOrigin(0.5);
    this.promptText.setVisible(false);

    // Enable prompt after delay
    this.time.delayedCall(PROMPT_DELAY_MS, () => {
      this.canAdvance = true;
      if (this.promptText) {
        this.promptText.setVisible(true);
        // Blink effect
        this.tweens.add({
          targets: this.promptText,
          alpha: { from: 1, to: 0.3 },
          duration: 600,
          yoyo: true,
          repeat: -1,
        });
      }
    });

    // Listen for any key press
    this.input.keyboard?.on('keydown', this.handleAdvance, this);

    // Listen for mouse/touch click
    this.input.on('pointerdown', this.handleAdvance, this);
  }

  private handleAdvance(): void {
    if (!this.canAdvance) {
      return;
    }

    // Prevent double-triggering
    this.canAdvance = false;

    // Clean up input listeners
    this.input.keyboard?.off('keydown', this.handleAdvance, this);
    this.input.off('pointerdown', this.handleAdvance, this);

    // Stop the game scene that's underneath
    this.scene.stop('GameScene');

    // Check if score qualifies for leaderboard
    if (isQualifyingScore(this.score)) {
      this.scene.start('InitialsEntryScene', {
        score: this.score,
        level: this.level,
      });
    } else {
      this.scene.start('LeaderboardScene');
    }
  }

  private cleanupObjects(): void {
    if (this.overlay) {
      this.overlay.destroy();
      this.overlay = null;
    }
    if (this.gameOverText) {
      this.gameOverText.destroy();
      this.gameOverText = null;
    }
    if (this.scoreText) {
      this.scoreText.destroy();
      this.scoreText = null;
    }
    if (this.levelText) {
      this.levelText.destroy();
      this.levelText = null;
    }
    if (this.promptText) {
      this.promptText.destroy();
      this.promptText = null;
    }
  }
}
